import {
    useNetworkStatus,
    type NetworkStatus,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import useErrorToast, { type ErrorToastProps } from "./useErrorToast";

type NotifyParams = ErrorToastProps & {
    /** Omise quand l'appelant dédoublonne lui-même. */
    queryKey?: QueryKey;
};

/**
 * Signale l'échec d'une query, sauf réseau non confirmé par le SDK, ou tentative précédente
 * déjà en échec (appelants qui passent une `queryKey`).
 *
 * `!== "online"` et non `=== "offline"` : pendant la salve de reconnexion le statut vaut
 * `checking`. Horodatages et non `status` : un refetch sans cache remet `status` à 'pending'.
 */
const useFetchErrorNotifier = () => {
    const queryClient = useQueryClient();
    const showErrorToast = useErrorToast();
    const networkStatus = useNetworkStatus();

    // Lu après l'await du queryFn : une closure capturerait la valeur du rendu.
    const networkStatusRef = useRef<NetworkStatus>(networkStatus.status);

    useEffect(() => {
        networkStatusRef.current = networkStatus.status;
    }, [networkStatus.status]);

    return ({ queryKey, ...toastProps }: NotifyParams): void => {
        if (networkStatusRef.current !== "online") {
            return;
        }

        const previousState = queryKey
            ? queryClient.getQueryState(queryKey)
            : undefined;
        const lastAttemptFailed =
            previousState !== undefined &&
            previousState.errorUpdatedAt > previousState.dataUpdatedAt;

        if (lastAttemptFailed) {
            return;
        }

        showErrorToast(toastProps);
    };
};

export default useFetchErrorNotifier;
